import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "axios"; // we will keep name simple
import toast from "react-hot-toast";
import Tesseract from "tesseract.js";
import { FaCamera, FaSpinner, FaFileInvoice } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number | string;
  company_name: string;
  state?: string;
  HSN_SAC?: string;
  GST_SLAB?: string;
  [key: string]: any;
}

interface Vendor {
  id: number | string;
  company_name: string;
  state?: string;
  gst_number?: string;
  [key: string]: any;
}

interface Setting {
  IGST: number;
  CGST: number;
  SGST: number;
  gemini_api_key?: string | null;
}

interface InvoiceRow {
  particulars: string;
  hsn_sac: string;
  amount: string;
}

const initialInvoiceRow: InvoiceRow = {
  particulars: "",
  hsn_sac: "",
  amount: "",
};

function parseHSNSAC(data: string | undefined): string[] {
  if (!data) return [];
  try {
    const arr = JSON.parse(data);
    if (Array.isArray(arr)) return arr;
    return [];
  } catch (err) {
    return [];
  }
}

function parseGSTSLAB(data: string | undefined): string[] {
  if (!data) return [];
  try {
    const arr = JSON.parse(data);
    if (Array.isArray(arr)) return arr;
    return [];
  } catch (err) {
    return [];
  }
}

function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const PurchaseInvoiceCreator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editInvoiceId = searchParams.get("invoice_id");

  const [companies, setCompanies] = useState<Company[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [settings, setSettings] = useState<Setting | null>(null);

  const [userRole, setUserRole] = useState<string | null>(null);
  const [userCompanyId, setUserCompanyId] = useState<string | number | null>(null);

  const [companyId, setCompanyId] = useState<string | number>("");
  const [vendorId, setVendorId] = useState<string | number>("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [rows, setRows] = useState<InvoiceRow[]>([{ ...initialInvoiceRow }]);
  
  const [sgstAmount, setSgstAmount] = useState<number>(0);
  const [cgstAmount, setCgstAmount] = useState<number>(0);
  const [igstAmount, setIgstAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [selectedGstRate, setSelectedGstRate] = useState<string>("");
  const [gstIncluded, setGstIncluded] = useState<boolean>(false);

  const [tdsRate, setTdsRate] = useState<string>("0");
  const [tdsAmount, setTdsAmount] = useState<string>("0");
  const [remarks, setRemarks] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState<string>("");
  const [paidStatus, setPaidStatus] = useState<number>(0);

  // Vendor creation state
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [newVendor, setNewVendor] = useState({ company_name: '', address: '', city: '', state: '', gst_number: '', pancard: '', email: '', opening_balance: '' });
  const [vendorSaving, setVendorSaving] = useState(false);

  // OCR state
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState("");
  const [scannedImage, setScannedImage] = useState<string>("");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // OCR Candidates and Interactive Selection State
  const [ocrCandidates, setOcrCandidates] = useState<{
    invoiceNoCandidates: string[];
    dateCandidates: string[];
    gstinCandidates: {
      gst: string;
      rawText: string;
      matchedType: 'company' | 'vendor' | 'new_vendor';
      matchedName: string;
      matchedId?: string | number;
      context?: string;
      address?: string;
      city?: string;
      state?: string;
      pan?: string;
      email?: string;
    }[];
    amountCandidates: { label: string; amount: number; rawText: string }[];
  }>({
    invoiceNoCandidates: [],
    dateCandidates: [],
    gstinCandidates: [],
    amountCandidates: []
  });
  const [showMappingPanel, setShowMappingPanel] = useState(false);

  const [mappedInvoiceNo, setMappedInvoiceNo] = useState("");
  const [mappedInvoiceDate, setMappedInvoiceDate] = useState("");
  const [mappedVendorId, setMappedVendorId] = useState<string | number>("");
  const [mappedCompanyId, setMappedCompanyId] = useState<string | number>("");
  const [mappedSubtotal, setMappedSubtotal] = useState<number>(0);
  const [mappedCgst, setMappedCgst] = useState<number>(0);
  const [mappedSgst, setMappedSgst] = useState<number>(0);
  const [mappedIgst, setMappedIgst] = useState<number>(0);
  const [mappedTotal, setMappedTotal] = useState<number>(0);

  const [ocrVendorName, setOcrVendorName] = useState("");
  const [ocrVendorAddress, setOcrVendorAddress] = useState("");
  const [ocrVendorCity, setOcrVendorCity] = useState("");
  const [ocrVendorState, setOcrVendorState] = useState("");
  const [ocrVendorGst, setOcrVendorGst] = useState("");
  const [ocrVendorPan, setOcrVendorPan] = useState("");
  const [ocrVendorEmail, setOcrVendorEmail] = useState("");
  const [ocrVendorOpeningBalance, setOcrVendorOpeningBalance] = useState("0");

  useEffect(() => {
    if (String(mappedVendorId).startsWith("new_")) {
      const gstToCreate = String(mappedVendorId).replace("new_", "");
      const newGstCandidate = ocrCandidates.gstinCandidates.find(g => g.gst === gstToCreate);
      const vendorName = newGstCandidate ? newGstCandidate.matchedName : `Vendor - ${gstToCreate}`;
      setOcrVendorName(vendorName);
      setOcrVendorGst(gstToCreate);
      setOcrVendorAddress(newGstCandidate?.address || "Scanned via OCR Selection");
      setOcrVendorCity(newGstCandidate?.city || "OCR City");
      setOcrVendorPan(newGstCandidate?.pan || (gstToCreate.length === 15 ? gstToCreate.substring(2, 12) : ""));
      setOcrVendorEmail(newGstCandidate?.email || "");
      setOcrVendorOpeningBalance("0");

      const stateCodeMap: Record<string, string> = {
        "07": "Delhi", "06": "Haryana", "09": "Uttar Pradesh", "27": "Maharashtra",
        "19": "West Bengal", "33": "Tamil Nadu", "29": "Karnataka", "24": "Gujarat",
        "32": "Kerala", "23": "Madhya Pradesh", "08": "Rajasthan", "03": "Punjab"
      };
      const prefix = gstToCreate.substring(0, 2);
      if (newGstCandidate?.state) {
        setOcrVendorState(newGstCandidate.state);
      } else {
        setOcrVendorState(stateCodeMap[prefix] || "Delhi");
      }
    } else {
      setOcrVendorName("");
      setOcrVendorGst("");
      setOcrVendorAddress("");
      setOcrVendorCity("");
      setOcrVendorState("");
      setOcrVendorPan("");
      setOcrVendorEmail("");
      setOcrVendorOpeningBalance("0");
    }
  }, [mappedVendorId, ocrCandidates.gstinCandidates]);

  const loadPdfJs = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if ((window as any).pdfjsLib) {
        resolve((window as any).pdfjsLib);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js";
      script.onload = () => {
        const pdfjsLib = (window as any).pdfjsLib;
        pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js";
        resolve(pdfjsLib);
      };
      script.onerror = () => reject(new Error("Failed to load PDF.js library"));
      document.body.appendChild(script);
    });
  };

  const convertPdfToImages = async (file: File): Promise<string[]> => {
    const pdfjsLib = await loadPdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdf.numPages;
    const imageUrls: string[] = [];

    for (let i = 1; i <= numPages; i++) {
      setOcrProgress(`Converting PDF page ${i} of ${numPages} to image...`);
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 2.0 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      if (!context) {
        throw new Error("Could not get 2d context for canvas");
      }
      
      await page.render({ canvasContext: context, viewport }).promise;
      imageUrls.push(canvas.toDataURL("image/png"));
    }
    return imageUrls;
  };

  const handleOcrFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setOcrLoading(true);
      setOcrProgress("Initializing scan...");

      // Automatically upload the file as attachment in the background
      const uploadData = new FormData();
      uploadData.append('file', file);
      try {
        const uploadRes = await axiosInstance.post(`${API_URL}/auth/upload-attachment`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (uploadRes.data && uploadRes.data.file_url) {
          setAttachmentUrl(uploadRes.data.file_url);
        }
      } catch (uploadErr) {
        console.error("Error auto-uploading file:", uploadErr);
      }

      try {
        let sources: any[] = [];
        if (file.type === "application/pdf") {
          sources = await convertPdfToImages(file);
          if (sources.length > 0) {
            setScannedImage(sources[0]); // Preview first page
          }
        } else {
          const previewUrl = URL.createObjectURL(file);
          setScannedImage(previewUrl);
          sources = [file];
        }

        let combinedText = "";
        for (let idx = 0; idx < sources.length; idx++) {
          setOcrProgress(`Starting OCR text recognition (Page ${idx + 1} of ${sources.length})...`);
          const result = await Tesseract.recognize(
            sources[idx],
            'eng',
            {
              logger: (m) => {
                if (m.status === 'recognizing text') {
                  setOcrProgress(`Scanning Page ${idx + 1}/${sources.length}: ${Math.round(m.progress * 100)}%`);
                } else {
                  setOcrProgress(`Page ${idx + 1}: ${m.status}`);
                }
              }
            }
          );
          combinedText += "\n" + result.data.text;
        }

        await autoProcessOcr(combinedText);
      } catch (err: any) {
        console.error("OCR Error:", err);
        toast.error(`Failed to scan invoice: ${err.message || 'Proceed manually.'}`);
      } finally {
        setOcrLoading(false);
        setOcrProgress("");
      }
    }
  };

  const autoProcessOcr = async (text: string) => {
    console.log("Extracted OCR Text for Candidates:", text);
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);

    let currentCompanies = companies;
    let currentVendors = vendors;
    try {
      const compRes = await axiosInstance.get(`${API_URL}/auth/companies`);
      currentCompanies = compRes.data;
      setCompanies(currentCompanies);
    } catch (e) {}
    try {
      const vendRes = await axiosInstance.get(`${API_URL}/auth/vendors`);
      currentVendors = vendRes.data;
      setVendors(currentVendors);
    } catch (e) {}

    // Check if Gemini API key is configured
    if (settings && settings.gemini_api_key) {
      setOcrProgress("Optimizing extraction using Gemini AI...");
      try {
        const geminiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${settings.gemini_api_key}`;
        const promptText = `
Analyze the following raw OCR text extracted from an invoice. Extract the following fields accurately and return ONLY a valid JSON object.
Do not wrap it in markdown block tags. Do not output anything other than raw JSON.
JSON Keys:
- invoice_no: string (the invoice/bill number)
- invoice_date: string (date of invoice in YYYY-MM-DD format)
- company_gstin: string (the buyer's 15-char GSTIN)
- vendor_gstin: string (the seller's 15-char GSTIN)
- vendor_name: string (the vendor/supplier name)
- vendor_address: string (the vendor's street address/location)
- vendor_city: string (the vendor's city)
- vendor_state: string (the vendor's state)
- vendor_pan: string (the vendor's 10-char PAN card number, usually characters 3 to 12 of GSTIN)
- vendor_email: string (the vendor's contact email)
- subtotal: number (taxable/base value before GST)
- cgst: number (Central GST amount)
- sgst: number (State GST amount)
- igst: number (Integrated GST amount)
- total_payable: number (grand total value)

Raw OCR Text:
${text}
        `;

        const response = await fetch(geminiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  { text: promptText }
                ]
              }
            ],
            generationConfig: {
              responseMimeType: "application/json"
            }
          })
        });

        if (response.ok) {
          const resJson = await response.json();
          const contentText = resJson.candidates?.[0]?.content?.parts?.[0]?.text;
          if (contentText) {
            const parsedGemini = JSON.parse(contentText);
            console.log("Gemini AI parsed result:", parsedGemini);

            // Populate mapped fields
            if (parsedGemini.invoice_no) setMappedInvoiceNo(parsedGemini.invoice_no);
            if (parsedGemini.invoice_date) setMappedInvoiceDate(parsedGemini.invoice_date);

            if (parsedGemini.company_gstin) {
              const matchedComp = currentCompanies.find(c => {
                const compGst = (c.gst_number || c.gst_uin || "").trim().toUpperCase();
                return compGst === parsedGemini.company_gstin.trim().toUpperCase();
              });
              if (matchedComp) setMappedCompanyId(matchedComp.id);
            }

            const localGstinCandidates: any[] = [];
            if (parsedGemini.vendor_gstin) {
              const matchedVend = currentVendors.find(v => {
                const vendGst = (v.gst_number || "").trim().toUpperCase();
                return vendGst === parsedGemini.vendor_gstin.trim().toUpperCase();
              });
              if (matchedVend) {
                setMappedVendorId(matchedVend.id);
                localGstinCandidates.push({
                  gst: parsedGemini.vendor_gstin.toUpperCase(),
                  rawText: parsedGemini.vendor_gstin.toUpperCase(),
                  matchedType: 'vendor',
                  matchedName: matchedVend.company_name,
                  matchedId: matchedVend.id,
                  context: "Parsed by Gemini AI"
                });
              } else {
                setMappedVendorId(`new_${parsedGemini.vendor_gstin.toUpperCase()}`);
                localGstinCandidates.push({
                  gst: parsedGemini.vendor_gstin.toUpperCase(),
                  rawText: parsedGemini.vendor_gstin.toUpperCase(),
                  matchedType: 'new_vendor',
                  matchedName: parsedGemini.vendor_name || `Vendor - ${parsedGemini.vendor_gstin.toUpperCase()}`,
                  address: parsedGemini.vendor_address || "",
                  city: parsedGemini.vendor_city || "",
                  state: parsedGemini.vendor_state || "",
                  pan: parsedGemini.vendor_pan || (parsedGemini.vendor_gstin.length === 15 ? parsedGemini.vendor_gstin.substring(2, 12) : ""),
                  email: parsedGemini.vendor_email || "",
                  context: "Parsed by Gemini AI"
                });
              }
            }

            if (parsedGemini.subtotal) setMappedSubtotal(Number(parsedGemini.subtotal));
            if (parsedGemini.cgst) setMappedCgst(Number(parsedGemini.cgst));
            if (parsedGemini.sgst) setMappedSgst(Number(parsedGemini.sgst));
            if (parsedGemini.igst) setMappedIgst(Number(parsedGemini.igst));
            if (parsedGemini.total_payable) setMappedTotal(Number(parsedGemini.total_payable));

            // Merge details to candidate state
            setOcrCandidates({
              invoiceNoCandidates: parsedGemini.invoice_no ? [parsedGemini.invoice_no] : [],
              dateCandidates: parsedGemini.invoice_date ? [parsedGemini.invoice_date] : [],
              gstinCandidates: localGstinCandidates,
              amountCandidates: [
                { label: "Sub Total (Taxable)", amount: Number(parsedGemini.subtotal || 0), rawText: "Deducted by Gemini AI" },
                { label: "Total Payable", amount: Number(parsedGemini.total_payable || 0), rawText: "Deducted by Gemini AI" },
                { label: "CGST Amount", amount: Number(parsedGemini.cgst || 0), rawText: "Deducted by Gemini AI" },
                { label: "SGST Amount", amount: Number(parsedGemini.sgst || 0), rawText: "Deducted by Gemini AI" },
                { label: "IGST Amount", amount: Number(parsedGemini.igst || 0), rawText: "Deducted by Gemini AI" }
              ]
            });

            toast.success("Gemini AI extraction completed successfully!");
            setShowMappingPanel(true);
            return;
          }
        }
      } catch (err) {
        console.error("Gemini Parse Error:", err);
      }
    }

    const parseOcrDateStr = (dStr: string): Date | null => {
      const clean = dStr.trim();
      const parts = clean.split(/[-/\s\.,]+/);
      if (parts.length === 3) {
        const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
        const fullMonths = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
        let monthVal = NaN;
        if (/^\d+$/.test(parts[1])) {
          monthVal = Number(parts[1]) - 1;
        } else if (months.includes(parts[1].toLowerCase().substring(0, 3))) {
          monthVal = months.indexOf(parts[1].toLowerCase().substring(0, 3));
        } else if (fullMonths.includes(parts[1].toLowerCase())) {
          monthVal = fullMonths.indexOf(parts[1].toLowerCase());
        }
        
        let year = NaN;
        let day = NaN;
        if (parts[0].length === 4) {
          year = Number(parts[0]);
          day = Number(parts[2]);
        } else if (parts[2].length === 4 || parts[2].length === 2) {
          year = Number(parts[2]);
          if (parts[2].length === 2) year += 2000;
          day = Number(parts[0]);
        }

        if (!isNaN(year) && !isNaN(monthVal) && monthVal >= 0 && !isNaN(day)) {
          return new Date(year, monthVal, day);
        }
      }
      const d = new Date(clean);
      return isNaN(d.getTime()) ? null : d;
    };

    const cleanNumber = (valStr: string): number => {
      const cleaned = valStr.replace(/[^0-9.]/g, '');
      const val = parseFloat(cleaned);
      return isNaN(val) ? 0 : val;
    };

    // --- 1. Invoice No Candidates ---
    const invNoSet = new Set<string>();
    for (const line of lines) {
      const match = line.match(/(?:invoice\s*no\.?|inv\s*no\.?|bill\s*no\.?|invoice\s*number|bill\s*number|vch\s*no\.?|voucher\s*no\.?|ref\s*no\.?|reference\s*no\.?|invoice\s*ref)\s*(?::|#|=|-|\.|\s)*\s*([a-z0-9\-\/\\_]+)/i);
      if (match && match[1] && match[1].length > 2) {
        invNoSet.add(match[1].trim());
      }
    }
    // Check lines for common pattern like INV/... or BILL/... or VCH/...
    for (const line of lines) {
      if (line.match(/(?:inv|bill|vch|invoice|voucher|ref)[a-z0-9\-\/\\_]+/i)) {
        const cleanInv = line.replace(/(?:invoice|invoice\s*no|inv\s*no|bill\s*no|voucher|vch|ref|number|no\.?|#|:|-)/gi, "").trim();
        if (cleanInv.length > 2 && cleanInv.length < 20) {
          invNoSet.add(cleanInv);
        }
      }
    }
    const codeMatches = text.match(/[a-z0-9\-\/\\_]{4,18}/gi);
    if (codeMatches) {
      codeMatches.forEach(m => {
        if (!/^\d+$/.test(m) && !m.includes(".") && m.length > 4 && !/invoice|date|gstin|buyer|seller|vendor/i.test(m)) {
          invNoSet.add(m.trim());
        }
      });
    }
    invNoSet.add(`OCR-${Date.now()}`);
    const invoiceNoCandidates = Array.from(invNoSet).slice(0, 6);

    // --- 2. Date Candidates ---
    const dateSet = new Set<string>();
    for (const line of lines) {
      const match = line.match(/(?:invoice\s*date|bill\s*date|date\s*of\s*issue|dated?|date|dt)\s*(?::|#|=|-|\.|\s)*\s*([0-9a-z\s\-\/\.,]+)/i);
      if (match && match[1]) {
        const parsed = parseOcrDateStr(match[1]);
        if (parsed) {
          dateSet.add(parsed.toISOString().split('T')[0]);
        }
      }
    }
    const generalDateMatches = text.match(/(\d{1,2})[-/\.](\d{1,2})[-/\.](\d{2,4})|(\d{4})[-/\.](\d{1,2})[-/\.](\d{1,2})/g);
    if (generalDateMatches) {
      generalDateMatches.forEach(dStr => {
        const parsed = parseOcrDateStr(dStr);
        if (parsed) {
          dateSet.add(parsed.toISOString().split('T')[0]);
        }
      });
    }
    dateSet.add(new Date().toISOString().split('T')[0]);
    const dateCandidates = Array.from(dateSet).slice(0, 6);

    // --- 3. GSTIN / Parties Candidates ---
    const gstinRegex = /[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}/gi;
    const detectedGstins = Array.from(new Set(text.match(gstinRegex) || [])).map(g => g.toUpperCase());

    const gstinCandidates: any[] = [];
    for (const gst of detectedGstins) {
      const matchedCompany = currentCompanies.find(c => (c.gst_number || c.gst_uin || "").trim().toUpperCase() === gst);
      const matchedVendor = currentVendors.find(v => (v.gst_number || "").trim().toUpperCase() === gst);

      const idx = text.indexOf(gst);
      let context = "";
      if (idx !== -1) {
        context = text.substring(Math.max(0, idx - 80), Math.min(text.length, idx + 100)).replace(/\n/g, ' ').trim();
      }

      if (matchedCompany) {
        gstinCandidates.push({
          gst,
          rawText: gst,
          matchedType: 'company',
          matchedName: matchedCompany.company_name,
          matchedId: matchedCompany.id,
          context
        });
      } else if (matchedVendor) {
        gstinCandidates.push({
          gst,
          rawText: gst,
          matchedType: 'vendor',
          matchedName: matchedVendor.company_name,
          matchedId: matchedVendor.id,
          context
        });
      } else {
        let vendorName = "";
        const gstLineIdx = lines.findIndex(l => l.toUpperCase().includes(gst));
        if (gstLineIdx !== -1) {
          for (let i = Math.max(0, gstLineIdx - 4); i < gstLineIdx; i++) {
            const line = lines[i];
            if (line.length > 4 && !line.includes(":") && !/\d{4}/.test(line) && !/invoice|date|tax|bill|gstin|buyer|consignee/i.test(line)) {
              vendorName = line.trim();
              break;
            }
          }
        }
        if (!vendorName) {
          vendorName = `Vendor - ${gst}`;
        }

        const emailMatch = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
        const emailVal = emailMatch ? emailMatch[0] : "";
        const panVal = gst.length === 15 ? gst.substring(2, 12) : "";

        let addressVal = "";
        if (gstLineIdx !== -1 && gstLineIdx + 1 < lines.length) {
          addressVal = lines[gstLineIdx + 1].trim();
        }

        gstinCandidates.push({
          gst,
          rawText: gst,
          matchedType: 'new_vendor',
          matchedName: vendorName,
          address: addressVal || "Scanned via OCR Selection",
          city: "OCR City",
          pan: panVal,
          email: emailVal,
          context
        });
      }
    }
    
    // --- 4. Tax & Amount Candidates ---
    const amountCandidates: any[] = [];
    const parsedAmountsSet = new Set<number>();
    
    for (const line of lines) {
      const numbers = line.match(/(?:rs\.?|inr|₹|:|-|\s)+\s*([\d,]+\.\d{2}|[\d,]+)/gi) || line.match(/[\d,]+\.\d{2}|[\d,]+/g);
      if (numbers) {
        for (const numStr of numbers) {
          const val = cleanNumber(numStr);
          if (val > 0 && !parsedAmountsSet.has(val) && val < 10000000) {
            parsedAmountsSet.add(val);
            
            let label = "Amount Option";
            const lowerLine = line.toLowerCase();
            if (lowerLine.includes("sub") || lowerLine.includes("taxable") || lowerLine.includes("base") || lowerLine.includes("assessable") || lowerLine.includes("taxable amount") || lowerLine.includes("taxable value")) {
              label = "Sub Total (Taxable)";
            } else if (lowerLine.includes("cgst") || lowerLine.includes("central tax") || lowerLine.includes("c.g.s.t")) {
              label = "CGST Amount";
            } else if (lowerLine.includes("sgst") || lowerLine.includes("state tax") || lowerLine.includes("s.g.s.t") || lowerLine.includes("utgst")) {
              label = "SGST Amount";
            } else if (lowerLine.includes("igst") || lowerLine.includes("integrated tax") || lowerLine.includes("i.g.s.t")) {
              label = "IGST Amount";
            } else if (lowerLine.includes("grand") || lowerLine.includes("total") || lowerLine.includes("net") || lowerLine.includes("payable") || lowerLine.includes("due") || lowerLine.includes("bill amount")) {
              label = "Total Payable";
            } else {
              label = line.substring(0, Math.min(line.length, 30));
            }

            amountCandidates.push({
              label,
              amount: val,
              rawText: line
            });
          }
        }
      }
    }

    setOcrCandidates({
      invoiceNoCandidates,
      dateCandidates,
      gstinCandidates,
      amountCandidates: amountCandidates.sort((a, b) => b.amount - a.amount)
    });

    if (invoiceNoCandidates.length > 0) setMappedInvoiceNo(invoiceNoCandidates[0]);
    if (dateCandidates.length > 0) setMappedInvoiceDate(dateCandidates[0]);

    // 1. Identify mapped Company & Vendor IDs
    let detectedCompanyId: string | number = "";
    let detectedVendorId: string | number = "";
    let companyGstStr = "";
    let vendorGstStr = "";

    for (const cand of gstinCandidates) {
      if (cand.matchedType === 'company') {
        detectedCompanyId = cand.matchedId;
        companyGstStr = cand.gst;
      } else if (cand.matchedType === 'vendor') {
        detectedVendorId = cand.matchedId;
        vendorGstStr = cand.gst;
      }
    }

    // If company GST is found, check if there's any other GSTIN and assume it's the vendor
    if (companyGstStr && !detectedVendorId) {
      const otherGst = gstinCandidates.find(g => g.gst !== companyGstStr);
      if (otherGst) {
        if (otherGst.matchedType === 'vendor') {
          detectedVendorId = otherGst.matchedId;
        } else {
          detectedVendorId = `new_${otherGst.gst}`;
        }
      }
    }

    // Set Company ID
    if (detectedCompanyId) {
      setMappedCompanyId(detectedCompanyId);
    } else if (currentCompanies.length > 0) {
      setMappedCompanyId(currentCompanies[0].id);
    }

    // Set Vendor ID
    if (detectedVendorId) {
      setMappedVendorId(detectedVendorId);
    } else if (gstinCandidates.length > 0) {
      const firstNewGst = gstinCandidates.find(g => g.matchedType === 'new_vendor');
      if (firstNewGst) {
        setMappedVendorId(`new_${firstNewGst.gst}`);
      } else if (currentVendors.length > 0) {
        setMappedVendorId(currentVendors[0].id);
      }
    } else if (currentVendors.length > 0) {
      setMappedVendorId(currentVendors[0].id);
    }

    // Find and map specific amounts with heuristics
    const totalPayableOption = amountCandidates.find(a => a.label === "Total Payable");
    const subtotalOption = amountCandidates.find(a => a.label === "Sub Total (Taxable)");
    const cgstOption = amountCandidates.find(a => a.label === "CGST Amount");
    const sgstOption = amountCandidates.find(a => a.label === "SGST Amount");
    const igstOption = amountCandidates.find(a => a.label === "IGST Amount");

    let finalTotal = totalPayableOption ? totalPayableOption.amount : 0;
    let finalSubtotal = subtotalOption ? subtotalOption.amount : 0;
    let finalCgst = cgstOption ? cgstOption.amount : 0;
    let finalSgst = sgstOption ? sgstOption.amount : 0;
    let finalIgst = igstOption ? igstOption.amount : 0;

    // Advanced heuristics for values
    const sortedAmts = [...amountCandidates].sort((a, b) => b.amount - a.amount);
    if (finalTotal === 0 && sortedAmts.length > 0) {
      finalTotal = sortedAmts[0].amount;
    }
    if (finalSubtotal === 0 && sortedAmts.length > 1) {
      const secondHighest = sortedAmts[1].amount;
      const ratio = secondHighest / finalTotal;
      if (ratio > 0.6 && ratio < 1.0) {
        finalSubtotal = secondHighest;
      }
    }
    const totalTaxes = finalCgst + finalSgst + finalIgst;
    if (finalSubtotal === 0 && finalTotal > 0 && totalTaxes > 0) {
      finalSubtotal = finalTotal - totalTaxes;
    }

    setMappedTotal(finalTotal);
    setMappedSubtotal(finalSubtotal);
    setMappedCgst(finalCgst);
    setMappedSgst(finalSgst);
    setMappedIgst(finalIgst);

    setShowMappingPanel(true);
    toast.success("Scanning complete! Map the fields in the panel below.");
  };

  const applyMappedFields = async () => {
    setInvoiceNo(mappedInvoiceNo);
    setInvoiceDate(mappedInvoiceDate);
    setCompanyId(mappedCompanyId);

    let finalVendorId = mappedVendorId;
    if (String(mappedVendorId).startsWith("new_")) {
      try {
        const newVendorObj = {
          company_name: ocrVendorName || `Vendor - ${ocrVendorGst}`,
          address: ocrVendorAddress || "Scanned via OCR Selection",
          city: ocrVendorCity || "OCR City",
          state: ocrVendorState || "Delhi",
          gst_number: ocrVendorGst,
          pancard: ocrVendorPan,
          email: ocrVendorEmail,
          opening_balance: ocrVendorOpeningBalance || "0"
        };
        await axiosInstance.post(`${API_URL}/auth/vendor`, newVendorObj);
        const freshVendors = await axiosInstance.get(`${API_URL}/auth/vendors`);
        setVendors(freshVendors.data);
        const created = freshVendors.data.find((v: any) => v.gst_number === ocrVendorGst);
        if (created) {
          finalVendorId = created.id;
          setVendorId(created.id);
          toast.success(`Successfully created and set vendor: ${newVendorObj.company_name}`);
        }
      } catch (err) {
        console.error("Auto Vendor Creation during apply error:", err);
        toast.error("Failed to auto-create vendor. Please check vendor creation form.");
      }
    } else {
      setVendorId(mappedVendorId);
    }

    const totalTax = mappedCgst + mappedSgst + mappedIgst;
    let baseValue = mappedSubtotal;
    if (!baseValue || baseValue === 0) {
      if (mappedTotal > 0) {
        baseValue = mappedTotal - totalTax;
      } else {
        baseValue = 100;
      }
    }
    setRows([{
      particulars: "Scanned Purchase Items",
      hsn_sac: "",
      amount: String(Math.round(baseValue))
    }]);

    let finalGstRate = "0";
    if (baseValue > 0) {
      const totalTax = mappedCgst + mappedSgst + mappedIgst;
      if (totalTax > 0) {
        const computedRate = Math.round((totalTax / baseValue) * 100);
        finalGstRate = String(computedRate);
      }
    }

    const standardRates = ["5", "12", "18", "28"];
    if (finalGstRate !== "0" && !standardRates.includes(finalGstRate)) {
      const closest = standardRates.reduce((prev, curr) => {
        return (Math.abs(Number(curr) - Number(finalGstRate)) < Math.abs(Number(prev) - Number(finalGstRate)) ? curr : prev);
      });
      finalGstRate = closest;
    }

    setSelectedGstRate(finalGstRate);
    setGstIncluded(false);

    setShowMappingPanel(false);
    toast.success("Fields applied! Scroll down to review the final details.");
  };

  useEffect(() => {
    setLoading(true);
    const fetchMetadata = async () => {
      try {
        const token = localStorage.getItem("token");
        const storedRole = localStorage.getItem("role");
        let decoded: any = null;
        if (token) {
          decoded = decodeToken(token);
        }
        setUserRole(storedRole || null);
        let compId: string | number | null = null;
        if (decoded) {
          compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
          setUserCompanyId(compId ? String(compId) : null);
        } else {
          setUserCompanyId(null);
        }

        const [compRes, vendRes, setRes] = await Promise.all([
          axiosInstance.get(`${API_URL}/auth/companies`),
          axiosInstance.get(`${API_URL}/auth/vendors`),
          axiosInstance.get(`${API_URL}/auth/setting`),
        ]);
        setCompanies(compRes.data);
        setVendors(vendRes.data);
        setSettings(setRes.data);

        if (storedRole !== "super-admin" && compId) {
          setCompanyId(String(compId));
        }

        if (editInvoiceId) {
          const invRes = await axiosInstance.get(`${API_URL}/auth/purchase-invoice/${editInvoiceId}`);
          const invData = invRes.data;
          
          setCompanyId(invData.Company_id || "");
          setVendorId(invData.Vendor_id || "");
          setInvoiceNo(invData.InvoiceNo || "");
          setAttachmentUrl(invData.attachment_url || "");
          
          if (invData.Date) {
            const formattedDate = new Date(invData.Date).toISOString().split('T')[0];
            setInvoiceDate(formattedDate);
          }

          setRemarks(invData.remarks || "");
          setTdsAmount(String(invData.TDSAmount || 0));
          setTdsRate("custom");
          setPaidStatus(invData.paid || 0);

          let parsedParts: string[] = [];
          let parsedHsns: string[] = [];
          let parsedAmounts: string[] = [];
          
          try { parsedParts = JSON.parse(invData.Particulars || "[]"); } catch (e) {}
          try { parsedHsns = JSON.parse(invData.HSN_SAC || "[]"); } catch (e) {}
          try { parsedAmounts = JSON.parse(invData.Amounts || "[]"); } catch (e) {}

          const newRows: InvoiceRow[] = [];
          const rowCount = Math.max(parsedParts.length, parsedHsns.length, parsedAmounts.length, 1);
          for (let i = 0; i < rowCount; i++) {
            newRows.push({
              particulars: parsedParts[i] || "",
              hsn_sac: parsedHsns[i] || "",
              amount: parsedAmounts[i] || "",
            });
          }
          setRows(newRows);

          // Calculate and reverse determine selected GST rate and inclusion status
          const totalGST = (parseFloat(invData.SGSTAmount) || 0) + (parseFloat(invData.CGSTAmount) || 0) + (parseFloat(invData.IGSTAmount) || 0);
          const baseSum = newRows.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
          if (totalGST > 0 && baseSum > 0) {
            const totalPayable = parseFloat(invData.Total_Amount) || 0;
            const calculatedGstRate = Math.round((totalGST / (totalPayable - totalGST)) * 100);
            setSelectedGstRate(String(calculatedGstRate));
            if (Math.abs(baseSum - totalPayable) < 5) {
              setGstIncluded(true);
            } else {
              setGstIncluded(false);
            }
          }
        }
      } catch (err) {
        setErrorMsg("Failed to fetch initial data.");
      } finally {
        setLoading(false);
      }
    };
    fetchMetadata();
  }, [editInvoiceId]);

  const selectedCompany = companies.find((c) => String(c.id) === String(companyId));
  const gstSlabOptions = selectedCompany ? parseGSTSLAB(selectedCompany.GST_SLAB) : [];
  const companyHSNOptions = selectedCompany ? parseHSNSAC(selectedCompany.HSN_SAC) : [];

  const calculateTaxesAndTotal = useCallback(() => {
    if (!companyId || !vendorId || companies.length === 0 || vendors.length === 0) {
      setSgstAmount(0);
      setCgstAmount(0);
      setIgstAmount(0);
      setTotalAmount(0);
      return;
    }

    const company = companies.find((c) => String(c.id) === String(companyId));
    const vendor = vendors.find((c) => String(c.id) === String(vendorId));

    if (!company || !vendor) return;

    const sum = rows.reduce((a, b) => a + (parseFloat(b.amount) || 0), 0);
    let gstRate = Number(selectedGstRate) || 0;

    let totalBase = sum;
    if (gstIncluded && gstRate > 0) {
      totalBase = sum / (1 + gstRate / 100);
    }

    let _sgst = 0, _cgst = 0, _igst = 0;

    if (
      company.state &&
      vendor.state &&
      company.state.trim().toLowerCase() === vendor.state.trim().toLowerCase()
    ) {
      _sgst = Math.round(((gstRate / 2) * totalBase) / 100);
      _cgst = Math.round(((gstRate / 2) * totalBase) / 100);
    } else {
      _igst = Math.round((gstRate * totalBase) / 100);
    }

    setSgstAmount(_sgst);
    setCgstAmount(_cgst);
    setIgstAmount(_igst);
    setTotalAmount(Math.round(totalBase + _sgst + _cgst + _igst));

    if (tdsRate && tdsRate !== "custom") {
      const rate = parseFloat(tdsRate) || 0;
      const calculatedTds = Math.round((totalBase * rate) / 100);
      setTdsAmount(calculatedTds.toString());
    }
  }, [companies, vendors, companyId, vendorId, rows, selectedGstRate, gstIncluded, tdsRate]);

  useEffect(() => {
    calculateTaxesAndTotal();
  }, [calculateTaxesAndTotal]);

  const saveInvoice = async () => {
    setSuccessMsg("");
    setErrorMsg("");

    const someRowHasAmount = rows.length > 0 && rows.some((row) => parseFloat(row.amount) > 0);
    if (!companyId || !vendorId || !invoiceNo || !invoiceDate || !someRowHasAmount) {
      toast.error("Please fill all required fields and at least one row with amount.");
      return;
    }

    setSubmitLoading(true);
    try {
      const particularsArr = rows.map((r) => r.particulars ?? "");
      const hsnArr = rows.map((r) => r.hsn_sac ?? "");
      const amountArr = rows.map((r) => (r.amount !== undefined && r.amount !== null ? String(r.amount) : ""));

      const payload = {
        Company_id: companyId,
        Vendor_id: vendorId,
        InvoiceNo: invoiceNo,
        Date: invoiceDate,
        SGSTAmount: sgstAmount,
        CGSTAmount: cgstAmount,
        IGSTAmount: igstAmount,
        Total_Amount: totalAmount,
        TDSAmount: Number(tdsAmount) || 0,
        Particulars: JSON.stringify(particularsArr),
        HSN_SAC: JSON.stringify(hsnArr),
        Amounts: JSON.stringify(amountArr),
        remarks: remarks.trim(),
        paid: paidStatus,
        attachment_url: attachmentUrl
      };

      if (editInvoiceId) {
        await axiosInstance.put(`${API_URL}/auth/purchase-invoice/${editInvoiceId}`, payload);
        toast.success("Purchase Invoice updated successfully!");
        setTimeout(() => {
          navigate("/purchase-invoices");
        }, 1500);
      } else {
        await axiosInstance.post(`${API_URL}/auth/purchase-invoice`, payload);
        toast.success("Purchase Invoice added successfully!");
        setRows([{ ...initialInvoiceRow }]);
        setInvoiceDate("");
        setCompanyId("");
        setVendorId("");
        setSgstAmount(0);
        setCgstAmount(0);
        setIgstAmount(0);
        setTotalAmount(0);
        setSelectedGstRate("");
        setInvoiceNo("");
        setTdsAmount("0");
        setTdsRate("0");
        setRemarks("");
        setScannedImage("");
        setAttachmentUrl("");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to save the purchase invoice.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveInvoice();
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <h2 className="text-3xl font-extrabold mb-2 text-blue-800 text-center tracking-tight">Record Purchase Invoice</h2>
      <p className="text-center text-gray-500 mb-8 text-md">Enter bills received from vendors and deduct TDS if applicable.</p>
      
      {/* OCR Scan Card */}
      <div className="bg-white border-2 border-dashed border-blue-200 rounded-3xl p-6 mb-8 hover:border-blue-400 transition-all duration-200 shadow-sm animate__animated animate__fadeIn">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className={`${scannedImage ? 'md:col-span-8' : 'md:col-span-12'} flex flex-col items-center justify-center space-y-3 text-center`}>
            <div className="p-4 bg-blue-50 rounded-full text-blue-600">
              {ocrLoading ? <FaSpinner className="animate-spin text-2xl" /> : <FaFileInvoice className="text-2xl" />}
            </div>
            <div>
              <h3 className="font-bold text-blue-900 text-base">Quick Scan Bill (OCR)</h3>
              <p className="text-xs text-gray-500 mt-1">Upload vendor invoice image or PDF to automatically pre-fill Invoice No., Date, Vendor GST, and Amount.</p>
            </div>
            <div className="w-full max-w-xs">
              <input
                type="file"
                accept="image/*,application/pdf"
                id="ocr-file-upload"
                disabled={ocrLoading}
                onChange={handleOcrFileChange}
                className="hidden"
              />
              <label
                htmlFor="ocr-file-upload"
                className={`inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-sm rounded-xl shadow cursor-pointer transition ${ocrLoading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <FaCamera /> {ocrLoading ? 'Analyzing...' : 'Scan Invoice (Image/PDF)'}
              </label>
              {ocrProgress && <p className="text-xs font-bold text-blue-700 mt-2">{ocrProgress}</p>}
            </div>
          </div>

          {scannedImage && (
            <div className="md:col-span-4 flex flex-col items-center justify-center border-t md:border-t-0 md:border-l border-blue-100 pt-4 md:pt-0 md:pl-4">
              <span className="text-xs font-bold text-blue-950 mb-2">Invoice Preview (Click to view)</span>
              <div 
                onClick={() => setIsPreviewModalOpen(true)}
                className="border border-blue-100 rounded-2xl overflow-hidden max-h-[180px] shadow-inner bg-slate-50 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all duration-200"
                title="Click to view full image"
              >
                <img src={scannedImage} alt="Scanned Invoice Preview" className="object-contain max-h-[170px] w-auto transition-transform hover:scale-105 duration-200" />
              </div>
              <button
                type="button"
                onClick={applyMappedFields}
                disabled={ocrLoading}
                className="mt-3 w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white font-bold text-xs rounded-xl shadow-md uppercase transition duration-150"
              >
                Apply Scanned Details
              </button>
              <button 
                type="button" 
                onClick={() => { setScannedImage(""); setIsPreviewModalOpen(false); }}
                className="text-[10px] text-red-500 hover:underline mt-2 font-semibold"
              >
                Remove Preview
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Interactive OCR Mapping Panel */}
      {showMappingPanel && (
        <div className="bg-gradient-to-tr from-slate-900 to-indigo-950 text-white rounded-3xl p-8 mb-8 shadow-2xl border border-indigo-500/30 animate__animated animate__fadeIn">
          <div className="flex justify-between items-center mb-6 border-b border-indigo-500/20 pb-4">
            <div>
              <h3 className="text-xl font-bold text-indigo-200">Select & Map Scanned Invoice Fields</h3>
              <p className="text-xs text-indigo-300 mt-1">Review OCR candidates and match them to form fields.</p>
            </div>
            <button 
              type="button" 
              onClick={() => setShowMappingPanel(false)}
              className="text-xs text-indigo-400 hover:text-white bg-indigo-900/50 hover:bg-indigo-900 px-3 py-1.5 rounded-xl transition"
            >
              Cancel Mapping
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Invoice No Candidate Selection */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-indigo-300 mb-2">Invoice Number Candidate *</label>
              <select 
                value={mappedInvoiceNo} 
                onChange={(e) => setMappedInvoiceNo(e.target.value)} 
                className="w-full bg-slate-900 border-2 border-indigo-500/20 rounded-xl p-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
              >
                {ocrCandidates.invoiceNoCandidates.map((cand, idx) => (
                  <option key={idx} value={cand}>{cand}</option>
                ))}
              </select>
              <div className="mt-2">
                <input 
                  type="text" 
                  value={mappedInvoiceNo} 
                  onChange={(e) => setMappedInvoiceNo(e.target.value)} 
                  placeholder="Or type custom invoice number..." 
                  className="w-full bg-slate-900/50 border border-indigo-500/10 rounded-lg p-2 text-xs text-indigo-200 placeholder-indigo-500 focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Date Candidate Selection */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-indigo-300 mb-2">Invoice Date Candidate *</label>
              <select 
                value={mappedInvoiceDate} 
                onChange={(e) => setMappedInvoiceDate(e.target.value)} 
                className="w-full bg-slate-900 border-2 border-indigo-500/20 rounded-xl p-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
              >
                {ocrCandidates.dateCandidates.map((cand, idx) => (
                  <option key={idx} value={cand}>{cand}</option>
                ))}
              </select>
              <div className="mt-2">
                <input 
                  type="date" 
                  value={mappedInvoiceDate} 
                  onChange={(e) => setMappedInvoiceDate(e.target.value)} 
                  className="w-full bg-slate-900/50 border border-indigo-500/10 rounded-lg p-2 text-xs text-indigo-200 focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Vendor (From) Candidate Selection */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-indigo-300 mb-2">Vendor (From) / GSTIN Candidate *</label>
              <select 
                value={mappedVendorId} 
                onChange={(e) => setMappedVendorId(e.target.value)} 
                className="w-full bg-slate-900 border-2 border-indigo-500/20 rounded-xl p-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
              >
                <option value="">-- Choose Vendor --</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.company_name} ({v.gst_number || 'No GSTIN'})</option>
                ))}
                {ocrCandidates.gstinCandidates.filter(g => g.matchedType === 'new_vendor').map((g, idx) => (
                  <option key={idx} value={`new_${g.gst}`}>[CREATE NEW] {g.matchedName} ({g.gst})</option>
                ))}
              </select>

              {String(mappedVendorId).startsWith("new_") && (
                <div className="mt-4 p-4 bg-indigo-950/40 border border-indigo-500/25 rounded-2xl space-y-3">
                  <span className="text-xs uppercase font-bold text-pink-400 block mb-1">New Vendor Details (Edit as needed):</span>
                  <div>
                    <label className="block text-[10px] text-indigo-300 mb-1">Vendor Name *</label>
                    <input 
                      type="text" 
                      value={ocrVendorName} 
                      onChange={(e) => setOcrVendorName(e.target.value)} 
                      className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-indigo-300 mb-1">GSTIN</label>
                      <input 
                        type="text" 
                        value={ocrVendorGst} 
                        onChange={(e) => setOcrVendorGst(e.target.value.toUpperCase())} 
                        className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-indigo-300 mb-1">State</label>
                      <input 
                        type="text" 
                        value={ocrVendorState} 
                        onChange={(e) => setOcrVendorState(e.target.value)} 
                        className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-indigo-300 mb-1">City</label>
                      <input 
                        type="text" 
                        value={ocrVendorCity} 
                        onChange={(e) => setOcrVendorCity(e.target.value)} 
                        className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-indigo-300 mb-1">Address</label>
                      <input 
                        type="text" 
                        value={ocrVendorAddress} 
                        onChange={(e) => setOcrVendorAddress(e.target.value)} 
                        className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] text-indigo-300 mb-1">PAN</label>
                      <input 
                        type="text" 
                        value={ocrVendorPan} 
                        onChange={(e) => setOcrVendorPan(e.target.value.toUpperCase())} 
                        className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                        placeholder="10-char PAN"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-indigo-300 mb-1">Email</label>
                      <input 
                        type="email" 
                        value={ocrVendorEmail} 
                        onChange={(e) => setOcrVendorEmail(e.target.value)} 
                        className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                        placeholder="email@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-indigo-300 mb-1">Opening Balance</label>
                      <input 
                        type="number" 
                        value={ocrVendorOpeningBalance} 
                        onChange={(e) => setOcrVendorOpeningBalance(e.target.value)} 
                        className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              )}

              {ocrCandidates.gstinCandidates.length > 0 && (
                <div className="mt-2 bg-indigo-950/50 rounded-xl p-3 border border-indigo-500/15">
                  <span className="text-[10px] uppercase font-bold text-indigo-400 block mb-1">Detected GSTINs & Context:</span>
                  {ocrCandidates.gstinCandidates.map((g, idx) => (
                    <div key={idx} className="text-xs text-indigo-200 mb-1 leading-snug">
                      <strong>{g.gst}</strong>: <span className="text-indigo-400 italic">"...{g.context}..."</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Company (Bill To) Candidate Selection */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-indigo-300 mb-2">Company (Bill To) *</label>
              <select 
                value={mappedCompanyId} 
                onChange={(e) => setMappedCompanyId(e.target.value)} 
                className="w-full bg-slate-900 border-2 border-indigo-500/20 rounded-xl p-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none"
              >
                <option value="">-- Choose Company --</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name} ({c.gst_number || c.gst_uin || 'No GSTIN'})</option>
                ))}
              </select>
            </div>

            {/* Tax & Amounts Section */}
            <div className="md:col-span-2 bg-slate-950/70 border border-indigo-500/10 rounded-2xl p-5 space-y-4">
              <h4 className="text-sm font-bold text-indigo-300 border-b border-indigo-500/15 pb-2">Map Invoice Values (Subtotal, GST, Total Payable)</h4>
              
              <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-indigo-400 mb-1">Sub Total (Taxable)</label>
                  <input 
                    type="number" 
                    value={mappedSubtotal} 
                    onChange={(e) => setMappedSubtotal(parseFloat(e.target.value) || 0)} 
                    className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-indigo-400 mb-1">CGST Amount</label>
                  <input 
                    type="number" 
                    value={mappedCgst} 
                    onChange={(e) => setMappedCgst(parseFloat(e.target.value) || 0)} 
                    className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-indigo-400 mb-1">SGST Amount</label>
                  <input 
                    type="number" 
                    value={mappedSgst} 
                    onChange={(e) => setMappedSgst(parseFloat(e.target.value) || 0)} 
                    className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-indigo-400 mb-1">IGST Amount</label>
                  <input 
                    type="number" 
                    value={mappedIgst} 
                    onChange={(e) => setMappedIgst(parseFloat(e.target.value) || 0)} 
                    className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-indigo-400 mb-1">Total Payable</label>
                  <input 
                    type="number" 
                    value={mappedTotal} 
                    onChange={(e) => setMappedTotal(parseFloat(e.target.value) || 0)} 
                    className="w-full bg-slate-900 border border-indigo-500/25 rounded-lg p-2 text-xs text-white"
                  />
                </div>
              </div>

              {ocrCandidates.amountCandidates.length > 0 && (
                <div>
                  <span className="text-[10px] uppercase font-bold text-indigo-400 block mb-2">Select from Extracted Numeric Candidates:</span>
                  <div className="flex flex-wrap gap-2 max-h-[160px] overflow-y-auto p-1 bg-slate-900/60 rounded-xl">
                    {ocrCandidates.amountCandidates.map((c, idx) => (
                      <div key={idx} className="bg-indigo-950/40 hover:bg-indigo-900/30 border border-indigo-500/10 rounded-xl px-3 py-1.5 flex items-center gap-2 transition text-xs">
                        <span className="font-semibold text-indigo-300">{c.label}:</span>
                        <span className="font-bold text-white">₹{c.amount}</span>
                        <div className="flex items-center gap-1 border-l border-indigo-500/10 pl-2">
                          <button 
                            type="button" 
                            onClick={() => setMappedSubtotal(c.amount)}
                            className="px-1.5 py-0.5 text-[10px] bg-indigo-900/60 text-indigo-200 rounded hover:bg-indigo-800"
                            title="Set as Subtotal"
                          >
                            Base
                          </button>
                          <button 
                            type="button" 
                            onClick={() => setMappedTotal(c.amount)}
                            className="px-1.5 py-0.5 text-[10px] bg-emerald-950/60 text-emerald-200 rounded hover:bg-emerald-800"
                            title="Set as Total"
                          >
                            Total
                          </button>
                          <button 
                            type="button" 
                            onClick={() => setMappedCgst(c.amount)}
                            className="px-1.5 py-0.5 text-[10px] bg-amber-950/60 text-amber-200 rounded hover:bg-amber-800"
                            title="Set as CGST"
                          >
                            CGST
                          </button>
                          <button 
                            type="button" 
                            onClick={() => setMappedSgst(c.amount)}
                            className="px-1.5 py-0.5 text-[10px] bg-amber-950/60 text-amber-200 rounded hover:bg-amber-800"
                            title="Set as SGST"
                          >
                            SGST
                          </button>
                          <button 
                            type="button" 
                            onClick={() => setMappedIgst(c.amount)}
                            className="px-1.5 py-0.5 text-[10px] bg-amber-950/60 text-amber-200 rounded hover:bg-amber-800"
                            title="Set as IGST"
                          >
                            IGST
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <button 
              type="button" 
              onClick={applyMappedFields}
              className="px-8 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-extrabold rounded-2xl shadow-lg hover:shadow-indigo-500/30 transition transform hover:-translate-y-0.5 uppercase text-xs tracking-wider"
            >
              Apply Scanned Data & Generate Purchase Invoice
            </button>
          </div>
        </div>
      )}

      {/* Full-view Scanned Document Modal */}
      {isPreviewModalOpen && scannedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate__animated animate__fadeIn"
          onClick={() => setIsPreviewModalOpen(false)}
        >
          <div 
            className="relative bg-white rounded-3xl p-3 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto flex flex-col justify-center items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsPreviewModalOpen(false)}
              className="absolute top-4 right-4 bg-slate-900/80 hover:bg-slate-950 text-white rounded-full p-2 h-9 w-9 flex items-center justify-center font-bold text-lg shadow transition focus:outline-none"
            >
              ✕
            </button>
            <img 
              src={scannedImage} 
              alt="Full-view Scanned Document" 
              className="max-h-[82vh] w-auto object-contain rounded-2xl" 
            />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-gradient-to-tr from-white via-blue-50 to-blue-100 shadow-xl border border-blue-200 rounded-3xl p-9 space-y-8 transition-all">
        <div className="grid md:grid-cols-2 gap-7">
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">Select Company *</label>
            {userRole !== "super-admin" && userCompanyId ? (
              <select
                value={String(userCompanyId)}
                disabled
                className="form-select w-full border-2 border-blue-200 rounded-xl bg-slate-100 text-gray-500 p-2 cursor-not-allowed"
              >
                {companies.filter(c => String(c.id) === String(userCompanyId)).map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            ) : (
              <select value={companyId} onChange={e => setCompanyId(e.target.value)} required className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2">
                <option value="">-- Select Company --</option>
                {companies.map(c => <option value={c.id} key={c.id}>{c.company_name}</option>)}
              </select>
            )}
          </div>
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">Select Vendor *</label>
            <select value={vendorId} onChange={e => setVendorId(e.target.value)} required className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2">
              <option value="">-- Select Vendor --</option>
              {vendors.map(v => <option value={v.id} key={v.id}>{v.company_name} {v.gst_number ? `(${v.gst_number})` : ''}</option>)}
            </select>
            <button type="button" onClick={() => setShowVendorForm(!showVendorForm)}
              className="mt-2 px-4 py-1.5 bg-gradient-to-r from-green-600 to-emerald-500 text-white text-sm rounded-lg shadow font-semibold hover:from-green-500 hover:to-emerald-400 transition">
              {showVendorForm ? '✕ Cancel' : '+ Create New Vendor'}
            </button>
          </div>
        </div>

        {/* Inline Vendor Creation Form */}
        {showVendorForm && (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 space-y-4 shadow-inner">
            <h3 className="text-green-900 font-bold text-lg">New Vendor</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Company / Vendor Name *</label>
                <input type="text" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.company_name}
                  onChange={e => setNewVendor({...newVendor, company_name: e.target.value})} placeholder="Vendor name" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">GST Number</label>
                <input type="text" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.gst_number}
                  onChange={e => setNewVendor({...newVendor, gst_number: e.target.value})} placeholder="GSTIN" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
                <input type="text" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.address}
                  onChange={e => setNewVendor({...newVendor, address: e.target.value})} placeholder="Address" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">City</label>
                <input type="text" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.city}
                  onChange={e => setNewVendor({...newVendor, city: e.target.value})} placeholder="City" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">State *</label>
                <input type="text" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.state}
                  onChange={e => setNewVendor({...newVendor, state: e.target.value})} placeholder="State" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">PAN</label>
                <input type="text" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.pancard}
                  onChange={e => setNewVendor({...newVendor, pancard: e.target.value})} placeholder="PAN Number" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                <input type="email" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.email}
                  onChange={e => setNewVendor({...newVendor, email: e.target.value})} placeholder="Email" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Opening Balance (₹)</label>
                <input type="number" step="any" className="form-input w-full rounded-lg border border-green-300 p-2" value={newVendor.opening_balance}
                  onChange={e => setNewVendor({...newVendor, opening_balance: e.target.value})} placeholder="0.00" />
              </div>
            </div>
            <button type="button" disabled={vendorSaving || !newVendor.company_name}
              onClick={async () => {
                setVendorSaving(true);
                try {
                  await axiosInstance.post(`${API_URL}/auth/vendor`, newVendor);
                  const res = await axiosInstance.get(`${API_URL}/auth/vendors`);
                  setVendors(res.data);
                  setNewVendor({ company_name: '', address: '', city: '', state: '', gst_number: '', pancard: '', email: '', opening_balance: '' });
                  setShowVendorForm(false);
                  toast.success('Vendor created successfully!');
                } catch (err: any) {
                  toast.error(err?.response?.data?.error || 'Failed to create vendor.');
                } finally {
                  setVendorSaving(false);
                }
              }}
              className="px-6 py-2 bg-green-600 text-white rounded-xl font-semibold shadow hover:bg-green-500 transition disabled:opacity-50">
              {vendorSaving ? 'Saving...' : 'Save Vendor'}
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-7">
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">Invoice No. *</label>
            <input type="text" className="form-input w-full rounded-xl border-2 border-gray-200 bg-white p-2 text-blue-900" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} required placeholder="Vendor's Invoice No." />
          </div>
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">Invoice Date *</label>
            <input type="date" className="form-input w-full rounded-xl border-2 border-gray-200 bg-white p-2" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} required />
          </div>
          <div>
            <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">Attachment (PDF/Image)</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={async (e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    const formData = new FormData();
                    formData.append('file', file);
                    const toastId = toast.loading("Uploading attachment...");
                    try {
                      const res = await axiosInstance.post(`${API_URL}/auth/upload-attachment`, formData, {
                        headers: {
                          'Content-Type': 'multipart/form-data'
                        }
                      });
                      if (res.data && res.data.file_url) {
                        setAttachmentUrl(res.data.file_url);
                        toast.success("Attachment uploaded successfully!", { id: toastId });
                      }
                    } catch (err) {
                      console.error(err);
                      toast.error("Failed to upload attachment.", { id: toastId });
                    }
                  }
                }}
                className="w-full border-2 border-gray-200 rounded-xl bg-white p-1 text-xs font-semibold text-blue-950 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              />
              {attachmentUrl && (
                <a
                  href={attachmentUrl.startsWith("http") ? attachmentUrl : `${API_URL}${attachmentUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-xs font-extrabold whitespace-nowrap hover:bg-blue-100 transition shadow-sm"
                  title="View Uploaded Attachment"
                >
                  📎 View
                </a>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">Particulars & Amount *</label>
          <div className="overflow-x-auto rounded-xl shadow border border-blue-100 bg-white/70">
            <table className="min-w-full rounded-xl">
              <thead>
                <tr className="bg-blue-100">
                  <th className="py-2 px-3 text-left font-bold text-blue-900 w-[50%]">Particulars</th>
                  <th className="py-2 px-3 text-left font-bold text-blue-900 w-[25%]">HSN/SAC</th>
                  <th className="py-2 px-3 text-left font-bold text-blue-900 w-[20%]">Amount (₹)</th>
                  <th className="py-2 px-3 w-[5%]"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="even:bg-blue-50">
                    <td className="p-2">
                      <input type="text" className="form-input w-full rounded bg-blue-50/70 border border-blue-200" value={row.particulars} onChange={(e) => { const r = [...rows]; r[i].particulars = e.target.value; setRows(r); }} required />
                    </td>
                    <td className="p-2">
                      <input type="text" className="form-input w-full rounded bg-blue-50/70 border border-blue-200" value={row.hsn_sac} onChange={(e) => { const r = [...rows]; r[i].hsn_sac = e.target.value; setRows(r); }} required />
                    </td>
                    <td className="p-2">
                      <input type="number" className="form-input w-full rounded bg-blue-50/70 border border-blue-200" value={row.amount} onChange={(e) => { const r = [...rows]; r[i].amount = e.target.value; setRows(r); }} min="0" step="any" required />
                    </td>
                    <td className="p-2 text-center align-middle">
                      {rows.length > 1 && <button type="button" onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="text-red-500 bg-red-50 rounded-full px-3 py-1 font-bold shadow-sm">&times;</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => setRows([...rows, { ...initialInvoiceRow }])} className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow font-semibold">+ Add More</button>
        </div>

        <div className="grid md:grid-cols-2 gap-7">
          <div>
             <label className="font-semibold text-blue-900 block mb-2 mt-1 uppercase tracking-wider">GST Rate</label>
              <select value={selectedGstRate} onChange={e => setSelectedGstRate(e.target.value)} className="form-select w-full border-2 border-green-300 rounded-xl bg-white p-2">
                <option value="">-- No GST / 0% --</option>
                {gstSlabOptions.map((rate, idx) => <option value={rate} key={idx}>{rate}%</option>)}
              </select>
              <div className="mt-3 flex items-center gap-3">
                <input type="checkbox" id="gstIncludedToggle" checked={gstIncluded} onChange={(e) => setGstIncluded(e.target.checked)} className="accent-blue-700 h-5 w-5" />
                <label htmlFor="gstIncludedToggle" className="text-blue-900/90 font-medium">Amount includes GST</label>
              </div>
          </div>
          <div>
             <label className="font-semibold text-blue-900 block mb-2 mt-1 uppercase tracking-wider">TDS Deduction</label>
             <div className="grid grid-cols-2 gap-4">
               <div>
                 <select value={tdsRate} onChange={e => setTdsRate(e.target.value)} className="form-select w-full border-2 border-red-300 rounded-xl bg-white p-2">
                   <option value="0">No TDS (0%)</option>
                   <option value="1">1%</option>
                   <option value="2">2%</option>
                   <option value="5">5%</option>
                   <option value="10">10%</option>
                   <option value="custom">Custom (₹)</option>
                 </select>
                 <p className="text-xs text-gray-500 mt-1">Select TDS rate %</p>
               </div>
               <div>
                 <input type="number" className="form-input w-full border-2 border-red-300 rounded-xl bg-white p-2" value={tdsAmount} onChange={e => { setTdsAmount(e.target.value); setTdsRate("custom"); }} min="0" step="any" placeholder="TDS Amount" disabled={tdsRate !== "custom"} />
                 <p className="text-xs text-gray-500 mt-1">TDS amount in ₹</p>
               </div>
             </div>
          </div>
        </div>

        <div>
          <label className="font-semibold text-blue-900 block mb-2 uppercase tracking-wider">Remarks</label>
          <textarea value={remarks} onChange={e => setRemarks(e.target.value)} className="form-textarea w-full border-2 border-yellow-200 rounded-xl p-2 bg-yellow-50 min-h-[60px]" rows={2} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 bg-gradient-to-br from-white to-blue-50 rounded-2xl p-5 border border-blue-100 shadow">
          <div>
            <div className="mb-3 flex justify-between border-b pb-2">
              <span className="text-blue-700 font-medium">SGST / CGST</span>
              <span className="text-blue-900 font-semibold">₹ {sgstAmount} / ₹ {cgstAmount}</span>
            </div>
            <div className="mb-3 flex justify-between border-b pb-2">
              <span className="text-blue-700 font-medium">IGST</span>
              <span className="text-blue-900 font-semibold">₹ {igstAmount}</span>
            </div>
            <div className="mb-3 flex justify-between border-b pb-2 text-red-600">
              <span className="font-medium">TDS Deducted</span>
              <span className="font-semibold">- ₹ {tdsAmount || 0}</span>
            </div>
            <div className="font-extrabold flex items-center justify-between text-xl pt-3 text-green-800">
              <span>Final Payable</span>
              <span>₹ {(totalAmount - (Number(tdsAmount) || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center pt-4">
          <button type="submit" className="px-7 py-2.5 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-lg hover:shadow-xl transition text-lg" disabled={submitLoading}>
            {submitLoading ? "Saving..." : "Save Purchase Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PurchaseInvoiceCreator;
